import cn from 'classnames';
import { QRCodeSVG } from 'qrcode.react';
import { FC } from 'react';
import { useIntl } from 'react-intl';

import { useMedia } from '@/shared/hooks';
import { Button, Modal } from '@/shared/ui';

import {
  MOBILE_QR_SIZE,
  MODAL_QR_SIZE,
  QR_QUIET_ZONE_MODULES,
} from '../../config';

import styles from './ReferralQrModal.module.scss';

type ReferralQrModalProps = {
  isOpen: boolean;
  link: string;
  displayedLink: string;
  className?: string;
  errorMessage?: string | null;
  onClose: () => void;
  onDownload: () => void;
};

export const ReferralQrModal: FC<ReferralQrModalProps> = (props) => {
  const {
    isOpen,
    link,
    displayedLink,
    className,
    errorMessage,
    onClose,
    onDownload,
  } = props;

  const { formatMessage } = useIntl();
  const { isMobile } = useMedia();

  return (
    <Modal
      isOpen={isOpen}
      onCancel={onClose}
      title={formatMessage({
        id: 'inviteColleague.qrModal.title',
        defaultMessage: 'Let your colleague scan this',
      })}
      className={className}
      containerClassName={styles.container}
      bodyClassName={styles.body}
      footer={
        <div className={styles.actions}>
          <Button variant="secondary" onClick={isMobile ? onDownload : onClose}>
            {isMobile
              ? formatMessage({
                  id: 'inviteColleague.saveImage',
                  defaultMessage: 'Save image',
                })
              : formatMessage({
                  id: 'global.close',
                  defaultMessage: 'Close',
                })}
          </Button>

          <Button onClick={isMobile ? onClose : onDownload}>
            {isMobile
              ? formatMessage({
                  id: 'global.done',
                  defaultMessage: 'Done',
                })
              : formatMessage({
                  id: 'inviteColleague.downloadQrCode',
                  defaultMessage: 'Download QR code',
                })}
          </Button>
        </div>
      }
      dataSection="invite-colleague-qr"
    >
      <p className={cn(styles.subtitle, 'p2')}>
        {formatMessage({
          id: 'inviteColleague.qrModal.subtitle',
          defaultMessage:
            'Their phone camera opens the same page as your link.',
        })}
      </p>

      <div className={styles.qr}>
        <QRCodeSVG
          value={link}
          size={isMobile ? MOBILE_QR_SIZE : MODAL_QR_SIZE}
          level="M"
          marginSize={QR_QUIET_ZONE_MODULES}
          title={formatMessage({
            id: 'inviteColleague.qrAlt',
            defaultMessage: 'QR code of your referral link',
          })}
        />
      </div>

      <p dir="ltr" className={cn(styles.link, 'p2')}>
        {displayedLink}
      </p>

      {errorMessage && (
        <p className={cn(styles.error, 'p3')} role="alert">
          {errorMessage}
        </p>
      )}
    </Modal>
  );
};
